import React, { createContext, useContext, useState, useEffect } from "react";
import { PermissionGroupResponse } from "../../../api/grpc/permissions/permissions";
import { useGroupsRequests } from "../../../api/grpc/permissions/useGroupsRequests";

interface GroupContextType {
  groups: PermissionGroupResponse[];
  loadingGroups: boolean;
  errorGroups: string | undefined;
  refetchGroups: () => Promise<void>;
}

const GroupContext = createContext<GroupContextType | undefined>(undefined);

export const useGroupContext = () => {
  const context = useContext(GroupContext);
  if (!context) {
    throw new Error("useGroupContext must be used within a GroupProvider");
  }
  return context;
};

export const GroupProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [groups, setGroups] = useState<PermissionGroupResponse[]>([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [errorGroups, setErrorGroups] = useState<string | undefined>(undefined);

  const { listPermissionGroups } = useGroupsRequests();

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      setLoadingGroups(true);

      const { response } = await listPermissionGroups();

      setGroups(response.groups);
    } catch (error: any) {
      setErrorGroups(error?.message);
    } finally {
      setLoadingGroups(false);
    }
  };

  return (
    <GroupContext.Provider
      value={{
        groups,
        loadingGroups,
        errorGroups,
        refetchGroups: fetchGroups,
      }}
    >
      {children}
    </GroupContext.Provider>
  );
};
